import React, { useEffect, useState } from 'react';
import { getToken } from '../../../utils/TokenUtils';
import { API_URL } from '../../../constants/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import '../css/ExportTab.css';

export default function ExportTab({ table }) {
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [selectedCols, setSelectedCols] = useState([]);
  const [exportFormat, setExportFormat] = useState('SQL');
  const [loading, setLoading] = useState(true);

  const token = getToken();

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/sql/tables/${table}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then((res) => res.json())
      .then((res) => {
        if (res?.columns) {
          setColumns(res.columns);
          setData(res.rows || []);
          setSelectedCols(res.columns);
        }
      })
      .catch((err) => console.error('Error fetching export data:', err))
      .finally(() => setLoading(false));
  }, [table, token]);

  const toggleColumn = (col) => {
    setSelectedCols((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]
    );
  };

  const selectAll = () => setSelectedCols(columns);
  const clearAll = () => setSelectedCols([]);

  const exportCSV = () => {
    if (!data.length || !selectedCols.length) return;

    const csvRows = [selectedCols.join(',')];
    for (const row of data) {
      const values = selectedCols.map(
        (col) => `"${(row[col] ?? '').toString().replace(/"/g, '""')}"`
      );
      csvRows.push(values.join(','));
    }

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    downloadBlob(blob, `${table}.csv`);
  };

  const exportSQL = () => {
    if (!data.length || !selectedCols.length) return;

    const sqlStatements = data.map((row) => {
      const values = selectedCols
        .map((col) => {
          const val = row[col];
          if (val === null || val === undefined) return 'NULL';
          return `'${val.toString().replace(/'/g, "''")}'`;
        })
        .join(', ');
      return `INSERT INTO \`${table}\` (\`${selectedCols.join('`, `')}\`) VALUES (${values});`;
    });

    const blob = new Blob([sqlStatements.join('\n')], { type: 'text/sql' });
    downloadBlob(blob, `${table}.sql`);
  };

  const exportPDF = () => {
    if (!data.length || !selectedCols.length) return;

    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Exported Data from ${table}`, 14, 15);

    const tableData = data.map((row) =>
      selectedCols.map((col) => (row[col] ?? '').toString())
    );

    doc.autoTable({
      head: [selectedCols],
      body: tableData,
      startY: 25,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [77, 144, 254] },
      alternateRowStyles: { fillColor: [248, 248, 248] },
    });

    doc.save(`${table}.pdf`);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = (e) => {
    e.preventDefault();
    if (exportFormat === 'CSV') exportCSV();
    else if (exportFormat === 'SQL') exportSQL();
    else if (exportFormat === 'PDF') exportPDF();
  };

  if (loading)
    return <div className="pma-loading">Loading export options...</div>;

  return (
    <div className="pma-export-wrapper">
      <form onSubmit={handleExport}>
        <div className="pma-export-title">
          Exporting rows from <span className="pma-tablename">`{table}`</span>{' '}
          table
        </div>

        {/* Format Selection Fieldset */}
        <fieldset className="pma-fieldset">
          <legend className="pma-legend">Format:</legend>
          <div className="pma-fieldset-content">
            <select
              className="pma-format-select"
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
            >
              <option value="SQL">SQL</option>
              <option value="CSV">CSV</option>
              <option value="PDF">PDF</option>
            </select>
          </div>
        </fieldset>

        {/* Column Selection Fieldset */}
        <fieldset className="pma-fieldset">
          <legend className="pma-legend">Columns to export:</legend>
          <div className="pma-fieldset-content">
            <div className="pma-col-actions">
              <button
                type="button"
                className="pma-link-btn"
                onClick={selectAll}
              >
                Select All
              </button>
              |
              <button type="button" className="pma-link-btn" onClick={clearAll}>
                Unselect All
              </button>
            </div>
            <div className="pma-columns-grid">
              {columns.map((col) => (
                <label key={col} className="pma-checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedCols.includes(col)}
                    onChange={() => toggleColumn(col)}
                  />
                  <span>{col}</span>
                </label>
              ))}
            </div>
          </div>
        </fieldset>

        {/* Bottom Toolbar */}
        <div className="pma-export-toolbar">
          <button type="submit" className="pma-btn pma-btn-primary">
            Go
          </button>
        </div>
      </form>
    </div>
  );
}
